import { Ring, skipGlobalLoading } from '@blocks/Loading'
import useLoading from '@hooks/useLoading'
import useScopedState from '@hooks/useScopedState'
import { ReactComponent as GalleryIcon } from '@icons/gallery.svg'
import { ReactComponent as UploadIcon } from '@icons/upload.svg'
import { modifier } from '@styles/mixins'
import { colors } from '@styles/vars'
import React from 'react'
import styled, { css } from 'styled-components'

export default function Image({ id, name, src, onChange, onClick, gallery, ...props }: Props) {
    const loading = useLoading()
    const [background, setBackground] = useScopedState<string | undefined>(src)

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        e.persist()
        
        loading.until(async () => {
            const prevented = await skipGlobalLoading(async () => onChange?.(e))
            if (prevented) return

            const file = e.target.files?.[0]
            if (!file) return setBackground(src)
            if (background) URL.revokeObjectURL(background)
            setBackground(URL.createObjectURL(file))
        }).catch(() => {
            e.target.value = ''
        })
    }

    return (
        <Wrapper background={background}>
            <input type="file" id={id} name={name} onChange={handleChange} onClick={onClick} accept="image/jpeg, image/png" {...props} />
            <label htmlFor={id}>
                {loading.state ? <Ring /> : <UploadIcon />}
            </label>
            {gallery?.visible && (
                <GalleryOption onClick={gallery.onClick}>
                    <GalleryIcon />
                    <small>Gallery</small>
                </GalleryOption>
            )}
        </Wrapper>
    )
}

/* STYLES */
const GalleryOption = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px 10px;
    background: #fff;
    border: 2px solid ${colors.border};

    cursor: pointer;
    transition: .2s ease-out;

    border-radius: 10px;

    svg {
        fill: ${colors.darkGrey};
        transition: .2s ease-out;
    }

    small {
        font-weight: 600;
        margin-left: 10px;
    }

    &:hover {
        p {
            color: ${colors.black};
        }

        svg {
            fill: ${colors.black};
        }

        background: ${colors.greyBackground};
    }
`

const Wrapper = styled.div<{ background?: string }>`
    display: flex;
    position: relative;
    height: 100%;
    width: 100%;

    input {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        opacity: 0;
        z-index: -1;
    }

    label {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center; 
        background: ${colors.white};
        border: 2px solid ${colors.border};
        width: 100%;
        height: 100%;
        border-radius: 10px;
        cursor: pointer;
        transition: .2s ease-out;
        font-weight: 600;
        color: ${colors.darkGrey};
        background-size: cover;
        background-position:  center center;
        background-repeat: no-repeat;

        ${props => {
            if (!props.background) return;

            return css`
                background-image: url(${props.background});
                border: 0;
            `
        }}

        p {
            font-weight: 600;
            margin-top: 5px;
        }

        svg {
            fill: ${colors.darkGrey};
            transition: .2s ease-out;

            ${modifier("background", css`
                display: none;
            `)}
        }
        
        &:hover {
            color: ${colors.black};
            border: 2px dashed ${colors.border};
            background: ${colors.greyBackground};

            ${props => {
                if (!props.background) return;

                return css`
                    background-size: cover;
                    background-position:  center center;
                    background-repeat: no-repeat;
                    background-image: url(${props.background});
                    border: 0;
                `
            }}

            svg {
                fill: ${colors.black};
            }

            p {
                color: ${colors.black};
            }
        }
    }
`

// TYPES
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
    id: string
    name: string,
    src?: string,
    onChange?(e: React.ChangeEvent<HTMLInputElement>): Promise<boolean | undefined>
    gallery?: {
        onClick(e: React.MouseEvent<HTMLDivElement>): void
        visible: boolean
    }
}